import { ApiRegion, config } from '@/config';

import {
  classifyFetchError,
  mergeAbortSignals,
  REGIONAL_REQUEST_TIMEOUT_MS,
  RegionalApiError,
  timeoutSignal,
} from './regionalApi';
import { clearRegionalAuthError, reportRegionalAuthError } from './regionalAuthErrors';
import { getUsStaffAccessToken, invalidateStaffRegionAccessToken } from './staffRegionAuth';

export type RegionalRequestOpts = {
  method?: string;
  params?: Record<string, string>;
  headers?: Record<string, string>;
  body?: BodyInit | null;
  signal?: AbortSignal;
  timeoutMs?: number;
};

export const buildRegionalUrl = (region: ApiRegion, path: string, params: Record<string, string> = {}): string => {
  const qs = new URLSearchParams(params).toString();
  return `${config.getApiUrlForRegion(region)}${path}${qs ? `?${qs}` : ''}`;
};

const sendRegionalRequest = async (
  canadaAccessToken: string,
  region: ApiRegion,
  path: string,
  opts: RegionalRequestOpts,
): Promise<Response> => {
  const { params, headers, signal, timeoutMs = REGIONAL_REQUEST_TIMEOUT_MS, ...fetchOpts } = opts;
  const url = buildRegionalUrl(region, path, params);
  const requestSignal = mergeAbortSignals([signal, timeoutSignal(timeoutMs)]);
  const send = async (accessToken: string) => {
    try {
      return await fetch(url, {
        ...fetchOpts,
        signal: requestSignal,
        headers: { ...headers, Authorization: `Bearer ${accessToken}` },
      });
    } catch (error) {
      throw classifyFetchError(error);
    }
  };

  const token =
    region === ApiRegion.US
      ? await getUsStaffAccessToken(canadaAccessToken, { signal: requestSignal })
      : canadaAccessToken;
  const response = await send(token);
  if (region !== ApiRegion.US || response.status !== 401) {
    return response;
  }

  // Only drop this Bearer if it is still current; concurrent 401s join one refresh.
  invalidateStaffRegionAccessToken(token);
  const retry = await send(await getUsStaffAccessToken(canadaAccessToken, { signal: requestSignal }));
  if (retry.status === 401) {
    throw new RegionalApiError({
      kind: 'unauthorized',
      status: 401,
      reminted: true,
      message: 'US API rejected the replacement staff token (401)',
    });
  }
  return retry;
};

/** Raw fetch on a regional host. Retries once on US 401 after clearing the staff token cache. */
export const regionalRequest = async (
  canadaAccessToken: string,
  region: ApiRegion,
  path: string,
  opts: RegionalRequestOpts = {},
): Promise<Response> => {
  try {
    const response = await sendRegionalRequest(canadaAccessToken, region, path, opts);
    if (response.status < 400) {
      clearRegionalAuthError(region);
    }
    return response;
  } catch (error) {
    reportRegionalAuthError(region, error);
    throw error;
  }
};

/** Host-relative path for a URL on this region's host. Never send a region token to another host. */
export const toRegionalPath = (apiUrl: string, src: string): string => {
  if (!src.startsWith(`${apiUrl}/`)) {
    throw new Error('URL is not on the region host');
  }
  return src.slice(apiUrl.length);
};
